/* ============================================
   BYTE-FLICKITY-MOBILE-NAV.JS
   Dot + arrow navigation for custom Flickity sliders, only below 768px
   Replaces the orange progress/scrollbar on mobile.
   ============================================ */

const MOBILE_BREAKPOINT = 768

function isMobile() {
  return window.matchMedia(`(max-width: ${MOBILE_BREAKPOINT - 1}px)`).matches
}

function createNavElement() {
  const nav = document.createElement('div')
  nav.className = 'flickity-mobile-nav'
  nav.setAttribute('role', 'tablist')
  nav.setAttribute('aria-label', 'Slider navigation')

  const prevBtn = document.createElement('button')
  prevBtn.type = 'button'
  prevBtn.className = 'flickity-mobile-nav__prev'
  prevBtn.setAttribute('aria-label', 'Previous')
  prevBtn.innerHTML = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M15 18l-6-6 6-6"/></svg>'

  const dotsWrap = document.createElement('div')
  dotsWrap.className = 'flickity-mobile-nav__dots'
  dotsWrap.setAttribute('role', 'tablist')

  const nextBtn = document.createElement('button')
  nextBtn.type = 'button'
  nextBtn.className = 'flickity-mobile-nav__next'
  nextBtn.setAttribute('aria-label', 'Next')
  nextBtn.innerHTML = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M9 18l6-6-6-6"/></svg>'

  nav.appendChild(prevBtn)
  nav.appendChild(dotsWrap)
  nav.appendChild(nextBtn)
  return { nav, prevBtn, dotsWrap, nextBtn }
}

function buildDots(container, count) {
  container.innerHTML = ''
  for (let i = 0; i < count; i++) {
    const dot = document.createElement('button')
    dot.type = 'button'
    dot.className = 'flickity-mobile-nav__dot'
    dot.setAttribute('role', 'tab')
    dot.setAttribute('aria-label', `Slide ${i + 1}`)
    dot.setAttribute('aria-selected', 'false')
    dot.dataset.index = String(i)
    container.appendChild(dot)
  }
}

function attachMobileNav(wrapper) {
  if (wrapper._flickityMobileNavAttached) return
  const slider = wrapper.querySelector('.custom-flickity-slider')
  if (!slider) return

  const flkty = typeof Flickity !== 'undefined' ? Flickity.data(slider) : null
  if (!flkty) return

  const slideCount = flkty.slides ? flkty.slides.length : 0
  if (slideCount <= 0) return

  const { nav, prevBtn, dotsWrap, nextBtn } = createNavElement()
  buildDots(dotsWrap, slideCount)

  function updateState() {
    const idx = flkty.selectedIndex
    dotsWrap.querySelectorAll('.flickity-mobile-nav__dot').forEach((dot, i) => {
      const selected = i === idx
      dot.classList.toggle('is-selected', selected)
      dot.setAttribute('aria-selected', selected ? 'true' : 'false')
    })
    prevBtn.disabled = idx <= 0
    nextBtn.disabled = idx >= slideCount - 1
  }

  prevBtn.addEventListener('click', () => {
    flkty.previous()
  })
  nextBtn.addEventListener('click', () => {
    flkty.next()
  })
  dotsWrap.addEventListener('click', (e) => {
    const dot = e.target.closest('.flickity-mobile-nav__dot')
    if (!dot) return
    const index = parseInt(dot.dataset.index, 10)
    if (!Number.isNaN(index)) flkty.select(index)
  })

  flkty.on('select', updateState)
  wrapper.appendChild(nav)
  updateState()

  wrapper._flickityMobileNavAttached = true
  wrapper._flickityMobileNavCleanup = () => {
    flkty.off('select', updateState)
    nav.remove()
    wrapper._flickityMobileNavAttached = false
    delete wrapper._flickityMobileNavCleanup
  }
}

function removeMobileNav(wrapper) {
  if (wrapper._flickityMobileNavCleanup) {
    wrapper._flickityMobileNavCleanup()
  }
}

function initWrappers() {
  const wrappers = document.querySelectorAll('.custom-flickity-wrapper')
  wrappers.forEach((wrapper) => {
    if (isMobile()) {
      if (wrapper._flickityMobileNavAttached) return
      const slider = wrapper.querySelector('.custom-flickity-slider')
      if (!slider) return
      const flkty = typeof Flickity !== 'undefined' ? Flickity.data(slider) : null
      if (flkty) {
        attachMobileNav(wrapper)
      }
    } else {
      removeMobileNav(wrapper)
    }
  })
}

function tryAttachDelayed() {
  const wrappers = document.querySelectorAll('.custom-flickity-wrapper')
  let attempts = 0
  const maxAttempts = 20
  const interval = setInterval(() => {
    if (!isMobile()) {
      clearInterval(interval)
      return
    }
    wrappers.forEach((wrapper) => {
      if (wrapper._flickityMobileNavAttached) return
      const slider = wrapper.querySelector('.custom-flickity-slider')
      if (!slider) return
      if (typeof Flickity !== 'undefined' && Flickity.data(slider)) {
        attachMobileNav(wrapper)
      }
    })
    attempts++
    if (attempts >= maxAttempts) clearInterval(interval)
  }, 100)
}

function attachScrollableNav(scrollEl) {
  const wrapper = scrollEl.closest('.scrollable-dots-wrapper') || scrollEl.parentElement
  if (!wrapper || wrapper._scrollableDotsAttached) return

  const maxDots = 10
  const minDots = 3
  const clientWidth = scrollEl.clientWidth
  const scrollWidth = scrollEl.scrollWidth
  const maxScroll = Math.max(0, scrollWidth - clientWidth)
  if (maxScroll <= 0) return

  const pageCount = Math.min(maxDots, Math.max(minDots, Math.ceil(scrollWidth / clientWidth)))
  const { nav, prevBtn, dotsWrap, nextBtn } = createNavElement()
  buildDots(dotsWrap, pageCount)

  function updateState() {
    const maxS = Math.max(0, scrollEl.scrollWidth - scrollEl.clientWidth)
    if (maxS <= 0) return
    const idx = Math.round((scrollEl.scrollLeft / maxS) * (pageCount - 1))
    const clamped = Math.max(0, Math.min(idx, pageCount - 1))
    dotsWrap.querySelectorAll('.flickity-mobile-nav__dot').forEach((dot, i) => {
      const selected = i === clamped
      dot.classList.toggle('is-selected', selected)
      dot.setAttribute('aria-selected', selected ? 'true' : 'false')
    })
    prevBtn.disabled = scrollEl.scrollLeft <= 0
    nextBtn.disabled = scrollEl.scrollLeft >= maxS - 1
  }

  const boundUpdate = updateState
  scrollEl.addEventListener('scroll', boundUpdate, { passive: true })

  function getScrollStep() {
    const first = scrollEl.querySelector('.grid-item, [class*="grid-item"], .custom-flickity-cell')
    if (first) return first.offsetWidth || first.getBoundingClientRect().width
    return scrollEl.clientWidth
  }

  prevBtn.addEventListener('click', () => {
    const step = getScrollStep()
    scrollEl.scrollBy({ left: -step, behavior: 'smooth' })
  })
  nextBtn.addEventListener('click', () => {
    const step = getScrollStep()
    scrollEl.scrollBy({ left: step, behavior: 'smooth' })
  })
  dotsWrap.addEventListener('click', (e) => {
    const dot = e.target.closest('.flickity-mobile-nav__dot')
    if (!dot) return
    const index = parseInt(dot.dataset.index, 10)
    if (Number.isNaN(index)) return
    const maxS = Math.max(0, scrollEl.scrollWidth - scrollEl.clientWidth)
    const target = pageCount <= 1 ? 0 : (index / (pageCount - 1)) * maxS
    scrollEl.scrollTo({ left: target, behavior: 'smooth' })
  })

  wrapper.appendChild(nav)
  updateState()

  wrapper._scrollableDotsAttached = true
  wrapper._scrollableDotsCleanup = () => {
    scrollEl.removeEventListener('scroll', boundUpdate)
    nav.remove()
    wrapper._scrollableDotsAttached = false
    delete wrapper._scrollableDotsCleanup
  }
}

function removeScrollableNav(wrapper) {
  if (wrapper._scrollableDotsCleanup) {
    wrapper._scrollableDotsCleanup()
  }
}

function initScrollableDots() {
  if (!isMobile()) {
    document.querySelectorAll('.scrollable-dots-wrapper').forEach(removeScrollableNav)
    return
  }
  document.querySelectorAll('[data-scrollable-dots-mobile]').forEach((scrollEl) => {
    const wrapper = scrollEl.closest('.scrollable-dots-wrapper') || scrollEl.parentElement
    if (!wrapper) return
    if (wrapper._scrollableDotsAttached) return
    const clientWidth = scrollEl.clientWidth
    const scrollWidth = scrollEl.scrollWidth
    if (scrollWidth <= clientWidth) return
    attachScrollableNav(scrollEl)
  })
}

function onResize() {
  if (!isMobile()) {
    document.querySelectorAll('.custom-flickity-wrapper').forEach(removeMobileNav)
    document.querySelectorAll('.scrollable-dots-wrapper').forEach(removeScrollableNav)
  } else {
    initWrappers()
    tryAttachDelayed()
    initScrollableDots()
  }
}

document.addEventListener('DOMContentLoaded', () => {
  if (isMobile()) {
    tryAttachDelayed()
    initScrollableDots()
    setTimeout(initScrollableDots, 600)
  }
  window.addEventListener('resize', onResize)
  document.addEventListener('click', (e) => {
    if (e.target.closest('.js-tab-trigger') && isMobile()) {
      setTimeout(initWrappers, 150)
    }
  })
})

document.addEventListener('scrollable-dots-init', () => {
  if (isMobile()) initScrollableDots()
})

if (typeof Shopify !== 'undefined' && Shopify.designMode) {
  document.addEventListener('shopify:section:load', () => {
    if (isMobile()) {
      setTimeout(initWrappers, 50)
      tryAttachDelayed()
      setTimeout(initScrollableDots, 100)
    }
  })
}

const scrollableObserver = new MutationObserver(() => {
  if (isMobile()) initScrollableDots()
})
scrollableObserver.observe(document.body, { childList: true, subtree: true })
